import { fetchJson } from "./http";
import type {
  GraphAggregation,
  HealthProbePoint,
  HealthProbeRunDetail,
  HealthSubsystemStatus,
  ServiceSlug,
} from "./types";

export interface GetHealthProbesResult {
  data: HealthProbePoint[];
  aggregation: GraphAggregation;
}

interface HealthProbesGraphResponse {
  data?: HealthProbePoint[] | null;
  aggregation?: GraphAggregation;
  timespan?: { from: string; to: string };
}

export async function getHealthProbes(
  serviceSlug: ServiceSlug,
  params: { start_time: string; end_time: string; health_check_id?: string },
  signal?: AbortSignal,
): Promise<GetHealthProbesResult> {
  const searchParams: Record<string, string> = {
    start_time: params.start_time,
    end_time: params.end_time,
  };
  if (params.health_check_id) {
    searchParams.health_check_id = params.health_check_id;
  }

  const raw = await fetchJson<HealthProbesGraphResponse | HealthProbePoint[]>(
    `/services/${encodeURIComponent(serviceSlug)}/health-probes`,
    { serviceSlug, signal, searchParams },
  );
  // Support unified body or legacy array response
  if (raw && typeof raw === "object" && "data" in raw && "aggregation" in raw) {
    const body = raw as HealthProbesGraphResponse;
    return {
      data: Array.isArray(body.data) ? body.data : [],
      aggregation: body.aggregation ?? { type: "none" },
    };
  }
  return {
    data: Array.isArray(raw) ? raw : [],
    aggregation: { type: "none" },
  };
}

export async function getHealthProbeRun(
  responseId: string,
  signal?: AbortSignal,
): Promise<HealthProbeRunDetail> {
  return fetchJson<HealthProbeRunDetail>(
    `/health/res/${encodeURIComponent(responseId)}`,
    { signal },
  );
}

export async function getHealthSubsystems(
  serviceSlug: ServiceSlug,
  healthCheckId: string,
  signal?: AbortSignal,
): Promise<HealthSubsystemStatus[]> {
  return fetchJson<HealthSubsystemStatus[]>(
    `/services/${encodeURIComponent(serviceSlug)}/health-checks/${encodeURIComponent(
      healthCheckId,
    )}/subsystems`,
    { signal },
  );
}